(() => {
  const roots = [...document.querySelectorAll("[data-story-field]")];
  if (!roots.length) return;

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
  const mobile = () => window.matchMedia("(max-width: 719px)").matches;

  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  const mulberry = (seed) => {
    let a = seed >>> 0;
    return () => {
      a = (a + 0x6d2b79f5) >>> 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const spectrum = (t) => {
    const stops = [
      [228, 138, 60],
      [196, 106, 170],
      [162, 86, 162],
      [111, 92, 196],
      [61, 184, 208],
    ];
    const x = clamp(t, 0, 0.999) * (stops.length - 1);
    const i = Math.floor(x);
    const f = x - i;
    const a = stops[i];
    const b = stops[i + 1] || a;
    return [
      lerp(a[0], b[0], f),
      lerp(a[1], b[1], f),
      lerp(a[2], b[2], f),
    ];
  };

  const rgba = (rgb, a) =>
    `rgba(${rgb[0] | 0},${rgb[1] | 0},${rgb[2] | 0},${a})`;

  const musicRidge = (t) => {
    const peak = Math.exp(-Math.pow((t - 0.52) * 2.15, 2));
    const climb = Math.pow(clamp(t / 0.52, 0, 1), 1.28);
    const shoulder = 0.24 * Math.exp(-Math.pow((t - 0.78) * 3.2, 2));
    return 0.16 + 0.78 * peak * (0.12 + 0.88 * climb) + shoulder;
  };

  const gauss = (rand) => {
    const u = Math.max(1e-6, rand());
    const v = Math.max(1e-6, rand());
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  };

  const landscape = (t, band) => {
    const wave = 0.48 + 0.22 * Math.sin(t * Math.PI * 1.85 + band);
    const crest = 0.12 * Math.sin(t * Math.PI * 3.4 + band * 0.7);
    const spark = 0.07 * Math.sin(t * 10.5 + band) * Math.exp(-Math.pow((t - 0.38) * 2.4, 2));
    return wave + crest + spark;
  };

  const makeMusic = (w, h, count, rand) => {
    const pts = [];
    for (let i = 0; i < count; i += 1) {
      const t = 0.01 + Math.pow(rand(), 0.82) * 0.98;
      const ridge = musicRidge(t);
      const crestY = (1 - ridge) * h * 0.9 + h * 0.04;
      const floorY = Math.min(h * 0.96, crestY + h * (0.18 + ridge * 0.42));
      const fill = Math.pow(rand(), 1.55);
      let y = lerp(crestY, floorY, fill) + gauss(rand) * (4 + ridge * 10);
      if (t > 0.38 && t < 0.68 && rand() < 0.16) {
        y = crestY - rand() * ridge * h * 0.55;
      }
      pts.push({
        t,
        x: t * w + gauss(rand) * (1.1 + ridge * 2.2),
        y,
        size: 0.4 + rand() * (ridge > 0.55 ? 1.45 : 1.05),
        glow: rand() < 0.05 + ridge * 0.04,
        color: spectrum(t * 0.94),
        a: 0.14 + rand() * 0.52 + ridge * 0.12,
      });
    }
    return pts;
  };

  const makeConverge = (w, h, count, rand) => {
    const cx = w * 0.5;
    const cy = Math.min(h * 0.76, h - 92);
    const sources = [
      { x: w * 0.17, y: h * 0.05, hue: 0.04 },
      { x: w * 0.5, y: h * 0.02, hue: 0.5 },
      { x: w * 0.83, y: h * 0.06, hue: 0.82 },
    ];
    const stream = Math.floor(count * 0.7);
    const knot = count - stream;
    const pts = [];

    for (let i = 0; i < stream; i += 1) {
      const src = sources[i % 3];
      pts.push({
        kind: "stream",
        src,
        p: Math.pow(rand(), 0.78),
        speed: 0.0009 + rand() * 0.0017,
        curve: 0.2 + rand() * 0.24,
        spread: (rand() - 0.5) * 11,
        size: 0.55 + rand() * 1.15,
        color: spectrum(src.hue + (rand() - 0.5) * 0.04),
        a: 0.36 + rand() * 0.5,
      });
    }

    for (let i = 0; i < knot; i += 1) {
      const phi = Math.acos(2 * rand() - 1);
      const theta = rand() * Math.PI * 2;
      const rad = Math.pow(rand(), 0.38);
      const petal = i % 3;
      pts.push({
        kind: "knot",
        theta,
        phi,
        rad,
        spin: 0.0025 + rand() * 0.007,
        rx: 92 + petal * 4,
        ry: 64 + petal * 3,
        size: 0.45 + rand() * 1.25,
        color: spectrum(0.08 + petal * 0.36 + rand() * 0.08),
        a: 0.22 + rand() * 0.54,
      });
    }

    return { pts, cx, cy };
  };

  const makeAmi = (w, h, count, rand) => {
    const pts = [];
    for (let i = 0; i < count; i += 1) {
      const t = rand();
      const band = (i % 5) * 0.48;
      const y = landscape(t, band) * h + gauss(rand) * h * 0.045;
      pts.push({
        t,
        x: t * w + gauss(rand) * 1.2,
        y,
        size: 0.4 + rand() * 1.25,
        glow: rand() < 0.06,
        color: spectrum(t * 0.96),
        a: 0.15 + rand() * 0.52,
        phase: rand() * Math.PI * 2,
      });
    }
    return pts;
  };

  const sizeCanvas = (canvas, box) => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.floor(box.width));
    const h = Math.max(1, Math.floor(box.height));
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d", { alpha: true });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx, w, h };
  };

  const dissolveEdges = (ctx, w, h, kind) => {
    ctx.save();
    ctx.globalCompositeOperation = "destination-in";
    if (kind === "ami") {
      const hg = ctx.createLinearGradient(0, 0, w, 0);
      hg.addColorStop(0, "rgba(0,0,0,0)");
      hg.addColorStop(0.1, "rgba(0,0,0,0.62)");
      hg.addColorStop(0.22, "rgba(0,0,0,1)");
      hg.addColorStop(0.78, "rgba(0,0,0,1)");
      hg.addColorStop(0.9, "rgba(0,0,0,0.62)");
      hg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = hg;
      ctx.fillRect(0, 0, w, h);
      const vg = ctx.createLinearGradient(0, 0, 0, h);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(0.18, "rgba(0,0,0,1)");
      vg.addColorStop(0.82, "rgba(0,0,0,1)");
      vg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, w, h);
    } else if (kind === "world") {
      const g = ctx.createRadialGradient(
        w * 0.62,
        h * 0.46,
        Math.min(w, h) * 0.12,
        w * 0.58,
        h * 0.58,
        Math.max(w, h) * 0.74
      );
      g.addColorStop(0, "rgba(0,0,0,1)");
      g.addColorStop(0.38, "rgba(0,0,0,0.94)");
      g.addColorStop(0.66, "rgba(0,0,0,0.38)");
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    } else if (kind === "music") {
      const g = ctx.createRadialGradient(
        w * 0.5,
        h * 0.58,
        Math.min(w, h) * 0.16,
        w * 0.5,
        h * 0.56,
        Math.max(w, h) * 0.78
      );
      g.addColorStop(0, "rgba(0,0,0,1)");
      g.addColorStop(0.4, "rgba(0,0,0,0.97)");
      g.addColorStop(0.68, "rgba(0,0,0,0.42)");
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    } else if (kind === "origin") {
      const g = ctx.createRadialGradient(
        w * 0.62,
        h * 0.58,
        Math.min(w, h) * 0.12,
        w * 0.6,
        h * 0.56,
        Math.max(w, h) * 0.68
      );
      g.addColorStop(0, "rgba(0,0,0,1)");
      g.addColorStop(0.48, "rgba(0,0,0,0.78)");
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    } else if (kind === "converge") {
      const g = ctx.createRadialGradient(
        w * 0.5,
        h * 0.62,
        Math.min(w, h) * 0.18,
        w * 0.5,
        h * 0.68,
        Math.max(w, h) * 0.86
      );
      g.addColorStop(0, "rgba(0,0,0,1)");
      g.addColorStop(0.7, "rgba(0,0,0,0.97)");
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    }
    ctx.restore();
  };

  const drawMusic = (ctx, w, h, pts, time, kind) => {
    ctx.clearRect(0, 0, w, h);
    const cx = kind === "music" ? w * 0.5 : w * 0.56;
    const cy = kind === "music" ? h * 0.58 : h * 0.5;
    const g = ctx.createLinearGradient(w * 0.18, h * 0.22, w * 0.88, h * 0.68);
    g.addColorStop(0, "rgba(228,138,60,0.07)");
    g.addColorStop(0.45, "rgba(162,86,162,0.16)");
    g.addColorStop(1, "rgba(61,184,208,0.08)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(cx, cy, w * 0.5, h * 0.44, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "lighter";
    pts.forEach((p) => {
      const y = p.y + Math.sin(time * 0.00045 + p.t * 14) * (p.glow ? 5 : 2.4);
      ctx.fillStyle = rgba(p.color, p.glow ? Math.min(0.9, p.a + 0.25) : p.a);
      const s = p.glow ? p.size * 2.2 : p.size;
      ctx.fillRect(p.x, y, s, s);
    });
    ctx.globalCompositeOperation = "source-over";
  };

  const drawConverge = (ctx, w, h, model, time) => {
    ctx.clearRect(0, 0, w, h);
    const { pts, cx, cy } = model;
    const core = ctx.createRadialGradient(cx, cy, 8, cx, cy, Math.min(w, h) * 0.42);
    core.addColorStop(0, "rgba(186,120,210,0.2)");
    core.addColorStop(0.4, "rgba(122,70,180,0.12)");
    core.addColorStop(1, "rgba(10,8,18,0)");
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(cx, cy, Math.min(w, h) * 0.42, 0, Math.PI * 2);
    ctx.fill();

    const sources = [
      { x: w * 0.17, y: h * 0.05, color: [228, 138, 60] },
      { x: w * 0.5, y: h * 0.02, color: [90, 200, 214] },
      { x: w * 0.83, y: h * 0.06, color: [196, 139, 192] },
    ];
    ctx.globalCompositeOperation = "lighter";
    sources.forEach((src, i) => {
      for (let s = 0; s < 22; s += 1) {
        ctx.beginPath();
        const spread = (s - 10.5) * 3.15;
        ctx.moveTo(src.x + spread * 0.1, src.y);
        ctx.bezierCurveTo(
          lerp(src.x, cx, 0.3) + spread * 0.95,
          lerp(src.y, cy, 0.2),
          lerp(src.x, cx, 0.7) + spread * 0.28,
          lerp(src.y, cy, 0.68),
          cx + Math.sin(time * 0.00035 + i) * 5,
          cy
        );
        ctx.strokeStyle = rgba(src.color, 0.055 + (s % 4) * 0.018);
        ctx.lineWidth = 0.9;
        ctx.stroke();
      }
    });

    if (w > 820) {
      ctx.setLineDash([2, 10]);
      ctx.beginPath();
      ctx.moveTo(w * 0.9, h * 0.16);
      ctx.quadraticCurveTo(w * 0.78, h * 0.28, cx + 70, cy - 24);
      ctx.strokeStyle = "rgba(196,139,192,0.28)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    const petals = [
      { color: [228, 138, 60], rot: -0.55 },
      { color: [90, 200, 214], rot: 0.55 },
      { color: [186, 120, 210], rot: 0 },
    ];
    petals.forEach((petal) => {
      ctx.strokeStyle = rgba(petal.color, 0.22);
      ctx.lineWidth = 1.15;
      for (let r = 0; r < 4; r += 1) {
        ctx.beginPath();
        ctx.ellipse(
          cx,
          cy,
          70 - r * 7,
          46 - r * 5,
          petal.rot + time * 0.00005,
          0,
          Math.PI * 2
        );
        ctx.stroke();
      }
    });

    ctx.strokeStyle = "rgba(186,140,220,0.12)";
    ctx.lineWidth = 1;
    for (let e = 0; e < 5; e += 1) {
      ctx.beginPath();
      ctx.ellipse(cx, cy, 88 - e * 6, 58 - e * 4, (e * 0.5) + time * 0.00008, 0, Math.PI * 2);
      ctx.stroke();
    }

    pts.forEach((p) => {
      let x;
      let y;
      let size = p.size;
      let alpha = p.a;
      if (p.kind === "stream") {
        const u = (p.p + time * p.speed) % 1;
        const pull = u * u * (3 - 2 * u);
        const midX = lerp(p.src.x, cx, 0.42) + p.spread * (1 - pull);
        const midY = lerp(p.src.y, cy, p.curve);
        x = (1 - pull) * (1 - pull) * p.src.x + 2 * (1 - pull) * pull * midX + pull * pull * cx;
        y = (1 - pull) * (1 - pull) * p.src.y + 2 * (1 - pull) * pull * midY + pull * pull * cy;
        size = p.size * (0.92 + pull * 0.45);
        alpha = Math.min(0.92, p.a * (0.82 + pull * 0.45));
      } else {
        const theta = p.theta + time * p.spin;
        x = cx + p.rad * p.rx * Math.sin(p.phi) * Math.cos(theta);
        y = cy + p.rad * p.ry * Math.sin(p.phi) * Math.sin(theta);
      }
      ctx.fillStyle = rgba(p.color, alpha);
      ctx.fillRect(x, y, size, size);
    });
    ctx.globalCompositeOperation = "source-over";
  };

  const drawAmi = (ctx, w, h, pts, time) => {
    ctx.clearRect(0, 0, w, h);
    const g = ctx.createLinearGradient(0, h * 0.3, w, h * 0.7);
    g.addColorStop(0, "rgba(228,138,60,0.08)");
    g.addColorStop(0.48, "rgba(162,86,162,0.16)");
    g.addColorStop(1, "rgba(61,184,208,0.08)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "lighter";
    pts.forEach((p) => {
      const y = p.y + Math.sin(time * 0.0004 + p.phase + p.t * 8) * 6;
      ctx.fillStyle = rgba(p.color, p.glow ? Math.min(0.85, p.a + 0.2) : p.a);
      const s = p.glow ? p.size * 2.1 : p.size;
      ctx.fillRect(p.x, y, s, s);
    });
    ctx.globalCompositeOperation = "source-over";
  };

  const fields = [];

  const setup = (root) => {
    const canvas = root.querySelector("canvas");
    if (!canvas) return;
    const kind = root.getAttribute("data-story-field");
    const state = {
      root,
      canvas,
      kind,
      model: null,
      w: 0,
      h: 0,
      visible: false,
    };

    const rebuild = () => {
      const box = root.getBoundingClientRect();
      if (box.width < 8 || box.height < 8) return;
      const { ctx, w, h } = sizeCanvas(canvas, box);
      state.ctx = ctx;
      state.w = w;
      state.h = h;
      const dense = mobile() ? 0.5 : 1;
      const rand = mulberry(kind === "music" ? 0xa11ce : kind === "converge" ? 0xc0a1e : kind === "origin" ? 0x0218 : kind === "world" ? 0x1336 : 0xa111);
      if (kind === "music") state.model = makeMusic(w, h, Math.floor(16000 * dense), rand);
      if (kind === "origin") state.model = makeMusic(w, h, Math.floor(900 * dense), rand);
      if (kind === "world") state.model = makeMusic(w, h, Math.floor(2200 * dense), rand);
      if (kind === "converge") state.model = makeConverge(w, h, Math.floor(12000 * dense), rand);
      if (kind === "ami") state.model = makeAmi(w, h, Math.floor(11000 * dense), rand);
      paint(state, reduce.matches ? 1800 : 0);
    };

    state.rebuild = rebuild;
    fields.push(state);
  };

  const paint = (state, time) => {
    if (!state.ctx || !state.model) return;
    if (state.kind === "music" || state.kind === "origin" || state.kind === "world") drawMusic(state.ctx, state.w, state.h, state.model, time, state.kind);
    if (state.kind === "converge") drawConverge(state.ctx, state.w, state.h, state.model, time);
    if (state.kind === "ami") drawAmi(state.ctx, state.w, state.h, state.model, time);
    dissolveEdges(state.ctx, state.w, state.h, state.kind);
  };

  roots.forEach(setup);

  let raf = 0;
  const tick = (now) => {
    fields.forEach((state) => {
      if (state.visible) paint(state, now);
    });
    raf = window.requestAnimationFrame(tick);
  };

  const start = () => {
    if (reduce.matches || raf) return;
    raf = window.requestAnimationFrame(tick);
  };

  const stop = () => {
    if (!raf) return;
    window.cancelAnimationFrame(raf);
    raf = 0;
  };

  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const state = fields.find((item) => item.root === entry.target);
          if (!state) return;
          state.visible = entry.isIntersecting;
          if (state.visible && !state.model) state.rebuild();
        });
        if (fields.some((item) => item.visible) && !reduce.matches) start();
        if (!fields.some((item) => item.visible)) stop();
      },
      { threshold: 0.08 }
    );
    fields.forEach((state) => io.observe(state.root));
  }

  const onResize = () => {
    fields.forEach((state) => state.rebuild());
  };
  window.addEventListener("resize", onResize, { passive: true });
  if ("ResizeObserver" in window) {
    const ro = new ResizeObserver(onResize);
    fields.forEach((state) => ro.observe(state.root));
  }

  reduce.addEventListener("change", () => {
    stop();
    fields.forEach((state) => state.rebuild());
    if (!reduce.matches && fields.some((item) => item.visible)) start();
  });

  window.requestAnimationFrame(() => fields.forEach((state) => state.rebuild()));
})();
